import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Tauri環境かどうかを検出
 */
export function isTauri(): boolean {
  if (typeof window === 'undefined') return false
  return '__TAURI_INTERNALS__' in window
}

/**
 * 外部URLを開く（Tauri環境とWeb環境の両方に対応）
 * Tauri環境では shell.open を使用、Web環境では window.open を使用
 */
export async function openExternalUrl(url: string): Promise<void> {
  if (isTauri()) {
    try {
      const { open } = await import('@tauri-apps/plugin-shell')
      await open(url)
    } catch (error) {
      console.error('Failed to open URL with Tauri shell:', error)
      // フォールバック
      window.open(url, '_blank')
    }
  } else {
    window.open(url, '_blank')
  }
}

// URL検出用の正規表現
const URL_REGEX = /(https?:\/\/[^\s<>"{}|\\^`\[\]]+)/g

export interface TextSegment {
  type: 'text' | 'url'
  content: string
}

/**
 * テキストをURL部分と通常テキスト部分に分割
 */
export function parseTextWithUrls(text: string): TextSegment[] {
  const segments: TextSegment[] = []
  let lastIndex = 0
  let match

  // 正規表現をリセット
  URL_REGEX.lastIndex = 0

  while ((match = URL_REGEX.exec(text)) !== null) {
    // URL前のテキスト
    if (match.index > lastIndex) {
      segments.push({
        type: 'text',
        content: text.slice(lastIndex, match.index)
      })
    }
    // URL部分
    segments.push({
      type: 'url',
      content: match[0]
    })
    lastIndex = match.index + match[0].length
  }

  // 残りのテキスト
  if (lastIndex < text.length) {
    segments.push({
      type: 'text',
      content: text.slice(lastIndex)
    })
  }

  return segments
}

/**
 * テキストにURLが含まれているかチェック
 */
export function containsUrl(text: string): boolean {
  URL_REGEX.lastIndex = 0
  return URL_REGEX.test(text)
}

/**
 * Inboxノートブックかどうかを判定
 */
export function isInboxNotebook(nb: { id: string; name: string }): boolean {
  return nb.name === "Inbox" || nb.id === "inbox" || nb.id?.startsWith("inbox-")
}

/**
 * ワークスペース固有のInbox IDを生成
 */
export function getInboxId(workspaceId: string): string {
  return `inbox-${workspaceId}`
}

/**
 * スロットリング関数 - 指定した間隔で最大1回だけ関数を実行
 * Phase 4: scroll/resizeイベントの最適化用
 */
export function throttle<T extends (...args: any[]) => void>(
  fn: T,
  delay: number
): T & { cancel: () => void } {
  let lastCall = 0;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const throttled = ((...args: Parameters<T>) => {
    const now = Date.now();
    const remaining = delay - (now - lastCall);

    if (remaining <= 0) {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      lastCall = now;
      fn(...args);
    } else if (!timeoutId) {
      timeoutId = setTimeout(() => {
        lastCall = Date.now();
        timeoutId = null;
        fn(...args);
      }, remaining);
    }
  }) as T & { cancel: () => void };

  throttled.cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  return throttled;
}
